/**
 * Berechnung von Arbeitsstunden und Lohn — pro Schicht und pro User aggregiert.
 *
 * WICHTIG:
 *  - Der Stundenlohn (`hourlyWage`) ist pro User individuell einstellbar
 *    und auf jeder Schicht als Snapshot gespeichert. Dadurch bleiben alte
 *    Schichten korrekt, auch wenn der User-Stundenlohn später geändert wird.
 *  - Pausen werden optional abgezogen (User-Setting `breakDeductionEnabled`).
 *  - Brutto = Netto (Minijob, keine Steuerabzüge).
 *
 * Pausen-Regel (gesetzlich, deutsches Arbeitszeitgesetz § 4):
 *   > 6 h Arbeitszeit → 30 min Pause
 *   > 9 h Arbeitszeit → 45 min Pause
 * Die Pause wird vom Bruttozeitfenster (Start → Ende) abgezogen, bevor
 * Bonus-Stunden draufkommen. Wer das Setting nicht aktiviert hat,
 * bekommt seine Schichtzeit voll bezahlt.
 *
 * Lohn-Modell:
 *   hourly:  Lohn = Stunden × hourlyWage
 *   salary:  Hybrid-Festgehalt. Bis zu monthlyTargetHours wird ein fester
 *            Monatslohn (monthlySalary) ausgezahlt. Stunden darüber werden
 *            mit hourlyWage zusätzlich vergütet.
 */

export interface ShiftLike {
  shiftDate: string;        // ISO-Date "YYYY-MM-DD"
  startTime: string;        // "HH:MM"
  endTime: string;          // "HH:MM"
  /**
   * Snapshot zum Zeitpunkt der Erfassung. `null`, wenn Phase G
   * (Entscheidung #6) den Lohn für den Betrachter verbirgt (fremde Schicht
   * ohne `managerCanSeeWages` am Standort). In allen Aufrufstellen dieses
   * Moduls sind das ausschließlich EIGENE Schichten des eingeloggten Users
   * (Eigen-Lohn wird nie verborgen) — `null` sollte hier also nie tatsächlich
   * vorkommen, wird aber defensiv wie 0 behandelt statt einen falschen Wert
   * vorzutäuschen oder zu crashen.
   */
  hourlyWage: number | null;
  /** Pauschale Zusatzstunden (z. B. Fahrzeit). Default 0 wenn nicht angegeben. */
  bonusHours?: number;
  /** Wenn true: Endzeit ist nur Platzhalter. */
  isOpenEnd?: boolean;
  status?: 'planned' | 'done' | 'cancelled';
  /** Abrechnung: ISO-Zeitstempel wenn abgerechnet, sonst null/undefined. */
  settledAt?: string | null;
  /**
   * Gesamtprüfung 11.09.2026: Status der Zusatzstunden. Solange ein Antrag
   * „pending“ ist, zählen die Zusatzstunden NICHT (erst nach Bestätigung
   * durch den Chef, Kapitel 11.5) — wie in Lohnrechnung und Dashboard.
   */
  bonusStatus?: 'none' | 'pending' | 'approved' | 'rejected';
  /** Tagesgeld-Entnahme: festgeschriebener, tatsächlich ausgezahlter Betrag. */
  dailyPayoutAmount?: number | null;
}

/**
 * True, wenn die Schicht eine offene Endzeit hat UND die Endzeit noch
 * nicht nachgetragen wurde (Platzhalter `endTime == startTime`).
 * Solche Schichten zählen mit 0 h in der Lohnberechnung.
 */
export function isOpenEndPending(
  shift: Pick<ShiftLike, 'startTime' | 'endTime' | 'isOpenEnd'>,
): boolean {
  return !!shift.isOpenEnd && shift.startTime === shift.endTime;
}

/**
 * Wie viele Minuten Pause werden bei einer gegebenen Brutto-Stundenzahl
 * gesetzlich abgezogen?
 *   > 6 h → 30 min, > 9 h → 45 min, sonst 0.
 */
export function breakDeductionMinutes(grossHours: number): number {
  if (grossHours > 9) return 45;
  if (grossHours > 6) return 30;
  return 0;
}

/**
 * Shadow (11.09.2026): „Pause gibt es nicht“ — der Pausen-Abzug ist
 * ausgeblendet und wirkt nirgends mehr. Die Einstellung bleibt technisch
 * erhalten (DB-Feld `breakDeductionEnabled`); zum Reaktivieren hier auf true
 * setzen und BREAK_DEDUCTION_FEATURE im Backend (reports.ts/shifts.ts) ebenso.
 */
export const BREAK_DEDUCTION_FEATURE = false;

export interface PayrollOptions {
  /**
   * Wenn true, wird der gesetzliche Pausen-Abzug auf die Schicht-Dauer
   * angewandt. Default false (Verhalten wie früher).
   */
  breakDeduction?: boolean;
}

/**
 * Dauer der Schicht aus Start/Ende (ohne Bonus). Wird z. B. fürs Anzeigen
 * der reinen Arbeitszeit verwendet, wenn Bonus separat ausgewiesen wird.
 * Bei offener Endzeit (noch nicht nachgetragen) → 0.
 * Mit `opts.breakDeduction` wird die gesetzliche Pause abgezogen.
 */
export function shiftDurationHours(
  shift: Pick<ShiftLike, 'startTime' | 'endTime' | 'isOpenEnd'>,
  opts: PayrollOptions = {},
): number {
  if (isOpenEndPending(shift)) return 0;
  const start = parseHHMM(shift.startTime);
  const end = parseHHMM(shift.endTime);
  let diffMin = end - start;
  if (diffMin < 0) diffMin += 24 * 60;
  let hours = diffMin / 60;
  if (BREAK_DEDUCTION_FEATURE && opts.breakDeduction) {
    hours = Math.max(0, hours - breakDeductionMinutes(hours) / 60);
  }
  return hours;
}

/**
 * Anzahl der LOHN-relevanten Stunden einer Schicht. Inkludiert die Pauschal-
 * Zusatzstunden (z. B. Fahrzeit), falls gesetzt. Dies ist der Wert, der in
 * Stunden-Summen und Lohn-Berechnungen verwendet wird.
 * Open-End-Schichten ohne nachgetragene Endzeit zählen mit 0.
 */
export function shiftHours(
  shift: Pick<ShiftLike, 'startTime' | 'endTime' | 'bonusHours' | 'isOpenEnd' | 'bonusStatus'>,
  opts: PayrollOptions = {},
): number {
  const bonus = shift.bonusStatus === 'pending' ? 0 : (shift.bonusHours ?? 0);
  return shiftDurationHours(shift, opts) + bonus;
}

/** Lohn einer einzelnen Schicht: Stunden × Stundenlohn (auf 2 Nachkommastellen). */
export function shiftWage(shift: ShiftLike, opts: PayrollOptions = {}): number {
  return roundCents(shiftHours(shift, opts) * (shift.hourlyWage ?? 0));
}

/** Summen-Statistik über mehrere Schichten. Optional auf Status "done" filtern. */
export interface PayrollSummary {
  totalHours: number;
  totalWage: number;
  shiftsCount: number;
}

export function summarize(
  shifts: ShiftLike[],
  options: { onlyDone?: boolean } & PayrollOptions = {},
): PayrollSummary {
  const filtered = options.onlyDone
    ? shifts.filter((s) => s.status === 'done')
    : shifts.filter((s) => s.status !== 'cancelled');

  let totalHours = 0;
  let totalWage = 0;
  for (const s of filtered) {
    const h = shiftHours(s, options);
    totalHours += h;
    // Gesamtprüfung 11.09.2026: bei einer Tagesgeld-Entnahme zählt der
    // tatsächlich ausgezahlte (festgeschriebene) Betrag — auch wenn sich
    // danach z. B. die Pausen-Einstellung ändert.
    totalWage += s.dailyPayoutAmount != null ? s.dailyPayoutAmount : (s.hourlyWage ?? 0) * h;
  }
  return {
    totalHours: roundCents(totalHours),
    totalWage: roundCents(totalWage),
    shiftsCount: filtered.length,
  };
}

/**
 * Aufteilung in „bereits erledigt" vs. „noch geplant".
 * Hilft der UI, beide Werte parallel anzuzeigen, damit der User weiß,
 * wieviel schon abgearbeitet und wieviel noch ansteht.
 *
 *  - done:     Schichten mit Status `done` (abgearbeitet)
 *  - planned:  Schichten mit Status `planned` (noch nicht erledigt)
 *  - combined: done + planned (= Monatsplan ohne Stornos)
 *
 * "cancelled"-Schichten zählen in keinem der drei Buckets.
 */
export interface SplitSummary {
  done: PayrollSummary;
  planned: PayrollSummary;
  combined: PayrollSummary;
}

export function summarizeSplit(
  shifts: ShiftLike[],
  options: PayrollOptions = {},
): SplitSummary {
  const done = shifts.filter((s) => s.status === 'done');
  const planned = shifts.filter((s) => s.status === 'planned');
  return {
    done: summarize(done, options),
    planned: summarize(planned, options),
    combined: summarize(shifts, options),
  };
}

export interface SettlementSummary {
  /** Bereits abgerechnete Schichten (settledAt gesetzt). */
  settled: PayrollSummary;
  /** Noch offene Schichten (nicht abgerechnet, nicht storniert). */
  open: PayrollSummary;
}

/**
 * Teilt die Schichten in „bereits abgerechnet" (settledAt gesetzt) und
 * „noch offen" auf. Stornierte Schichten zählen in keinem Bucket.
 * Nützlich für die Übersicht: wieviel Lohn ist schon abgerechnet und
 * wieviel steht noch offen.
 */
export function summarizeSettlement(
  shifts: ShiftLike[],
  options: PayrollOptions = {},
): SettlementSummary {
  const relevant = shifts.filter((s) => s.status !== 'cancelled');
  return {
    settled: summarize(relevant.filter((s) => !!s.settledAt), options),
    open: summarize(relevant.filter((s) => !s.settledAt), options),
  };
}

/** Gruppiert Schichten nach Monat ("YYYY-MM") und liefert Summary pro Monat. */
export function summarizeByMonth(shifts: ShiftLike[]): Map<string, PayrollSummary> {
  const buckets = new Map<string, ShiftLike[]>();
  for (const s of shifts) {
    const key = s.shiftDate.slice(0, 7); // "YYYY-MM"
    const arr = buckets.get(key) ?? [];
    arr.push(s);
    buckets.set(key, arr);
  }
  const result = new Map<string, PayrollSummary>();
  for (const [key, arr] of buckets) {
    result.set(key, summarize(arr));
  }
  return result;
}

// ---------------------------------------------------------------------------
// Aggregationen für die Monats-Übersicht
// ---------------------------------------------------------------------------

export interface ShiftLikeWithLocation extends ShiftLike {
  locationId: string;
}

/** Aufschlüsselung pro Standort (key = locationId). Ignoriert abgesagte Schichten. */
export function summarizeByLocation(
  shifts: ShiftLikeWithLocation[],
  options: PayrollOptions = {},
): Map<string, PayrollSummary> {
  const buckets = new Map<string, ShiftLikeWithLocation[]>();
  for (const s of shifts) {
    if (s.status === 'cancelled') continue;
    const arr = buckets.get(s.locationId) ?? [];
    arr.push(s);
    buckets.set(s.locationId, arr);
  }
  const result = new Map<string, PayrollSummary>();
  for (const [key, arr] of buckets) {
    result.set(key, summarize(arr, options));
  }
  return result;
}

/**
 * Liefert die ISO-Wochennummer eines Datums (Mo als erster Wochentag, deutsche Konvention).
 * Standard ISO-8601: Woche, in der der erste Donnerstag liegt = KW1.
 */
export function isoWeekNumber(isoDate: string): number {
  const d = new Date(`${isoDate}T00:00:00Z`);
  // Donnerstag der gleichen Woche (ISO-Trick)
  const day = (d.getUTCDay() + 6) % 7; // Mo=0
  d.setUTCDate(d.getUTCDate() - day + 3);
  const firstThursday = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  const firstThursdayDay = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - firstThursdayDay + 3);
  const diff = (d.getTime() - firstThursday.getTime()) / (7 * 24 * 3600 * 1000);
  return 1 + Math.round(diff);
}

export interface WeeklyEntry {
  /** ISO-Wochennummer */
  week: number;
  summary: PayrollSummary;
}

/**
 * Gruppiert Schichten nach ISO-Kalenderwoche. Sortiert aufsteigend.
 * Ignoriert abgesagte Schichten.
 */
export function summarizeByIsoWeek(shifts: ShiftLike[], options: PayrollOptions = {}): WeeklyEntry[] {
  const buckets = new Map<number, ShiftLike[]>();
  for (const s of shifts) {
    if (s.status === 'cancelled') continue;
    const wk = isoWeekNumber(s.shiftDate);
    const arr = buckets.get(wk) ?? [];
    arr.push(s);
    buckets.set(wk, arr);
  }
  return Array.from(buckets.entries())
    .map(([week, arr]) => ({ week, summary: summarize(arr, options) }))
    .sort((a, b) => a.week - b.week);
}

/**
 * Berechnet die prozentuale Differenz zwischen zwei Werten.
 * Liefert null wenn die Basis 0 ist (sonst Division durch 0).
 */
export function percentChange(current: number, previous: number): number | null {
  if (previous === 0) return null;
  return ((current - previous) / previous) * 100;
}

// ---------------------------------------------------------------------------
// Lohn-Modell (Hybrid-Festgehalt vs. reiner Stundenlohn)
// ---------------------------------------------------------------------------

export type SalaryMode = 'hourly' | 'salary';

export interface SalaryConfig {
  mode: SalaryMode;
  /** Siehe Kommentar an `ShiftLike.hourlyWage` — hier immer der EIGENE Lohn, nie verborgen. */
  hourlyWage: number | null;
  /** Nur bei mode='salary' relevant. Siehe Kommentar an `hourlyWage` — kann `null` sein (verborgen). */
  monthlySalary?: number | null;
  /** Nur bei mode='salary' relevant. Sollstunden im Abrechnungszeitraum. */
  monthlyTargetHours?: number;
}

export interface SalaryBreakdown {
  /** Was der User tatsächlich bekommt (gerundet auf Cents). */
  totalWage: number;
  /** Anteil aus dem Festgehalt (0 bei mode=hourly). */
  baseWage: number;
  /** Anteil aus Überstunden / reinen Stunden. */
  variableWage: number;
  /** Stunden über dem Sollstunden-Limit (bei mode=salary). */
  overtimeHours: number;
  /** Mode, der zur Berechnung benutzt wurde. */
  mode: SalaryMode;
}

/**
 * Berechnet den Lohn für eine Periode (z. B. einen Abrechnungszeitraum).
 *
 *   hourly: Lohn = hours × hourlyWage
 *   salary: Lohn = monthlySalary + max(0, hours − target) × hourlyWage
 *
 * Bei mode='salary' bekommt der Mitarbeiter mindestens das Festgehalt.
 * Auch wenn weniger als die Sollstunden gearbeitet wurden, wird der
 * volle monthlySalary ausgezahlt (klassisches Anstellungsverhältnis).
 */
export function calculatePeriodWage(
  hours: number,
  config: SalaryConfig,
): SalaryBreakdown {
  if (config.mode === 'salary') {
    const base = config.monthlySalary ?? 0;
    const target = config.monthlyTargetHours ?? 0;
    const overtime = Math.max(0, hours - target);
    const variable = roundCents(overtime * (config.hourlyWage ?? 0));
    return {
      mode: 'salary',
      baseWage: roundCents(base),
      variableWage: variable,
      overtimeHours: roundCents(overtime),
      totalWage: roundCents(base + variable),
    };
  }
  // hourly
  const wage = roundCents(hours * (config.hourlyWage ?? 0));
  return {
    mode: 'hourly',
    baseWage: 0,
    variableWage: wage,
    overtimeHours: 0,
    totalWage: wage,
  };
}

// ---------------------------------------------------------------------------
// Abrechnungszeitraum
// ---------------------------------------------------------------------------

/**
 * Ein konkreter Abrechnungszeitraum mit Anfang und Ende.
 * `from`/`to` sind ISO-Date-Strings (YYYY-MM-DD), beide inklusiv.
 */
export interface BillingCycle {
  /** Erstes Datum im Zeitraum (inklusiv). */
  from: string;
  /** Letztes Datum im Zeitraum (inklusiv). */
  to: string;
  /** Stable Key fürs Speichern/Vergleichen, z. B. "2026-05-15". */
  key: string;
  /** Lesbare Beschreibung, z. B. "15.04.–14.05.2026" oder "Mai 2026". */
  label: string;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function isoDate(d: Date): string {
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

function shortDate(d: Date): string {
  return `${pad2(d.getUTCDate())}.${pad2(d.getUTCMonth() + 1)}.${d.getUTCFullYear()}`;
}

/**
 * Berechnet den Abrechnungszeitraum für einen User anhand eines Stichtags.
 *
 *   startDay = 1  → klassischer Kalendermonat (z. B. 01.05.–31.05.)
 *   startDay = 15 → versetzter Zyklus (z. B. 15.04.–14.05.)
 *
 * `referenceDate` bestimmt, in welchen Zyklus der Stichtag fällt:
 *   - Heutiges Datum 12.05., startDay 15 → Zyklus 15.04.–14.05.
 *   - Heutiges Datum 20.05., startDay 15 → Zyklus 15.05.–14.06.
 *
 * Labels: bei startDay=1 wird "Mai 2026", sonst "15.04.–14.05.2026".
 */
export function billingCycleAt(
  startDay: number,
  referenceDate: Date = new Date(),
): BillingCycle {
  // Auf 1–28 begrenzen (gleiche Validierung wie im Backend).
  const day = Math.min(28, Math.max(1, Math.round(startDay)));

  // In UTC arbeiten, damit Sommerzeit-Switches nicht stören.
  const refY = referenceDate.getUTCFullYear();
  const refM = referenceDate.getUTCMonth();
  const refD = referenceDate.getUTCDate();

  // from-Datum: wenn refD >= startDay → aktueller Monat, sonst Vormonat
  let fromY = refY;
  let fromM = refM;
  if (refD < day) {
    fromM -= 1;
    if (fromM < 0) {
      fromM = 11;
      fromY -= 1;
    }
  }
  const fromDate = new Date(Date.UTC(fromY, fromM, day));
  // to-Datum: ein Monat später, einen Tag früher
  let toY = fromY;
  let toM = fromM + 1;
  if (toM > 11) {
    toM = 0;
    toY += 1;
  }
  const toDate = new Date(Date.UTC(toY, toM, day - 1));

  const fromIso = isoDate(fromDate);
  const toIso = isoDate(toDate);

  let label: string;
  if (day === 1) {
    // Klassischer Kalendermonat — nutze den Monatsnamen
    const MONTHS = [
      'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
      'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
    ];
    label = `${MONTHS[fromM]} ${fromY}`;
  } else {
    label = `${shortDate(fromDate)}–${shortDate(toDate)}`;
  }

  return {
    from: fromIso,
    to: toIso,
    key: fromIso,
    label,
  };
}

/**
 * Vorheriger / nächster Abrechnungszeitraum (delta = ±1, ±2, …).
 * Übergebe den aktuell angezeigten Zyklus, bekommst den verschobenen zurück.
 */
export function shiftBillingCycle(
  cycle: BillingCycle,
  startDay: number,
  delta: number,
): BillingCycle {
  // from + delta Monate als Referenz nehmen
  const [y, m, d] = cycle.from.split('-').map(Number);
  const ref = new Date(Date.UTC(y!, (m ?? 1) - 1 + delta, d ?? 1));
  return billingCycleAt(startDay, ref);
}

// ---------------------------------------------------------------------------
// Minijob-Grenze (Stand 2026: 603,00 € / Monat — gekoppelt an gesetzlichen
// Mindestlohn × 130h ÷ 12 Monate). Phase P (Kapitel 20.5): der tatsächlich
// verwendete Wert kommt pro Firma aus useMinijobLimit()/Company.minijobLimitEur
// — diese Konstante ist nur noch der Offline-/Erststart-Fallback.
// ---------------------------------------------------------------------------

export const MINIJOB_LIMIT_EUR = 603;

export type MinijobLevel = 'ok' | 'caution' | 'warning' | 'danger' | 'exceeded';

export interface MinijobStatus {
  level: MinijobLevel;
  limit: number;
  remaining: number;       // verbleibender Spielraum bis zur Grenze (kann negativ sein)
  percent: number;         // ausgeschöpfter Anteil 0..100+ (auf 1 Nachkommastelle gerundet)
  label: string;           // kurzer Text für die UI
}

/**
 * Liefert eine Bewertung des aktuellen Lohns gegenüber der Minijob-Grenze.
 *
 *   < 80 %        → ok        (grün)
 *   80–93 %       → caution   (gelb)
 *   93–97 %       → warning   (orange)
 *   97–100 %      → danger    (rot, dringend Schichten reduzieren)
 *   ≥ 100 %       → exceeded  (Grenze überschritten)
 */
export function minijobStatus(currentWage: number, limit = MINIJOB_LIMIT_EUR): MinijobStatus {
  const percent = limit > 0 ? (currentWage / limit) * 100 : 0;
  const remaining = limit - currentWage;

  let level: MinijobLevel;
  let label: string;
  if (percent >= 100) {
    level = 'exceeded';
    label = 'Minijob-Grenze überschritten';
  } else if (percent >= 97) {
    level = 'danger';
    label = 'Sehr nah an der Grenze';
  } else if (percent >= 93) {
    level = 'warning';
    label = 'Achtung — knapp unter der Grenze';
  } else if (percent >= 80) {
    level = 'caution';
    label = 'Im Auge behalten';
  } else {
    level = 'ok';
    label = 'Gut im Rahmen';
  }

  return {
    level,
    limit,
    remaining: roundCents(remaining),
    percent: Math.round(percent * 10) / 10,
    label,
  };
}

// ------------------- intern -------------------

function parseHHMM(s: string): number {
  const [hh, mm] = s.split(':').map(Number);
  return (hh ?? 0) * 60 + (mm ?? 0);
}

function roundCents(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Gesamtprüfung 11.09.2026 (spiegelt den Server): abrechenbar sind nur
 * Schichten bis einschließlich heute, deren Endzeit feststeht („Ware“-Schichten
 * erst nach dem Ausstempeln/Nachtragen).
 */
export function isSettleableNow(shift: Pick<ShiftLike, 'shiftDate' | 'startTime' | 'endTime' | 'isOpenEnd'>): boolean {
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  return shift.shiftDate <= today && !isOpenEndPending(shift);
}
